import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Toaster, toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { KeyRound, CheckCircle2, Eye, EyeOff, ShieldAlert } from "lucide-react";
import PasswordChecklist from "../components/PasswordChecklist";
import {
  MIN_PASSWORD_LEN,
  REQUIRED_CLASSES,
  OPERATOR_FORBIDDEN_SUBSTRINGS,
} from "../lib/passwordPolicy";

/**
 * Public reset-password page.
 *
 * Visual policy adapts to the target user:
 *  - Default: tenant policy (≥12 chars, 3 classes).
 *  - When ``/auth/reset-password-policy?token=...`` returns
 *    ``is_operator: true``, switches to operator policy (≥20 chars, 4
 *    classes, brand + email-derived substring block) and shows the
 *    "Operator account" banner so the user understands the stricter
 *    rules.
 *
 * Single "Show passwords" toggle controls both fields — simpler UX
 * than two separate toggles, keyboard accessible, ARIA-labelled.
 *
 * Privacy/security:
 *  - The toggle ONLY changes the input ``type`` attribute. Nothing is
 *    logged, stored, or sent to the server beyond the existing
 *    reset-password POST.
 *  - The backend policy endpoint never returns more than is_operator
 *    + min_length + required_classes for valid tokens, and returns the
 *    DEFAULT (tenant) policy for invalid/expired/used tokens, so this
 *    page can't be used to enumerate.
 */
export default function ResetPasswordPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  // Dynamic policy state — populated on mount from the policy endpoint.
  const [policy, setPolicy] = useState({
    isOperator: false,
    minLength: MIN_PASSWORD_LEN,
    requiredClasses: REQUIRED_CLASSES,
    policyEmail: "", // surfaced only for the local-part / domain block
  });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/auth/reset-password-policy?token=${encodeURIComponent(token)}`,
        );
        if (cancelled || !data?.ok) return;
        setPolicy({
          isOperator: !!data.is_operator,
          minLength: data.min_length || MIN_PASSWORD_LEN,
          requiredClasses: data.required_classes || REQUIRED_CLASSES,
          // We don't get the email back; the substring-rejection list
          // for operator uses the static brand list + (server-side) the
          // operator email. The UI also shows the dynamic operator's
          // local-part once the user types the operator email — but
          // since this page is post-link-click, we trust the static
          // list to surface the most likely violations.
          policyEmail: "",
        });
      } catch {
        // Stay on defaults; the actual reset attempt will surface any
        // backend rejection.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (user && user !== false) {
    return (
      <Navigate to={user.role === "admin" ? "/admin" : "/scan"} replace />
    );
  }

  const matchesOk = password.length > 0 && password === confirm;
  const tooShort = password.length > 0 && password.length < policy.minLength;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!token) {
      setErr("Missing reset token. Please use the link from your reset email.");
      return;
    }
    if (password.length < policy.minLength) {
      setErr(`Password must be at least ${policy.minLength} characters`);
      return;
    }
    if (password !== confirm) {
      setErr("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      setDone(true);
      toast.success("Password reset");
    } catch (e2) {
      const msg = formatApiErrorDetail(e2);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-stretch bg-paper"
      data-testid="reset-password-page"
      data-policy={policy.isOperator ? "operator" : "tenant"}
    >
      <div className="flex flex-col w-full md:w-[520px] bg-paper px-6 sm:px-12 py-10 md:py-14">
        <div className="flex items-center gap-2 mb-10">
          <div className="h-10 w-10 bg-brand rounded-sm flex items-center justify-center text-white font-display font-black text-xl">
            S
          </div>
          <div className="font-display font-black text-2xl tracking-tight">
            Shelf<span className="text-brand">Life</span>
          </div>
        </div>

        {done ? (
          <div className="max-w-md" data-testid="reset-success">
            <div className="data-sku mb-3">Password reset</div>
            <h1 className="font-display font-black text-4xl leading-[0.95] tracking-tighter mb-4">
              You&apos;re all set.
            </h1>
            <div className="flex items-start gap-2 bg-shelf-fresh-bg text-shelf-fresh px-4 py-3 rounded-sm mb-6">
              <CheckCircle2 size={18} className="mt-0.5" />
              <p className="text-sm">
                Your password has been updated. You can now sign in.
                {policy.isOperator && (
                  <>
                    {" "}
                    All prior operator sessions have been invalidated for
                    security.
                  </>
                )}
              </p>
            </div>
            <Link
              to="/login"
              className="btn-primary w-full inline-flex items-center justify-center"
              data-testid="reset-back-to-login"
            >
              Continue to sign in
            </Link>
          </div>
        ) : (
          <div className="max-w-md">
            <div className="data-sku mb-3">Reset password</div>
            <h1 className="font-display font-black text-4xl leading-[0.95] tracking-tighter mb-3">
              Choose a new password.
            </h1>

            {policy.isOperator && (
              <div
                className="flex items-start gap-2 bg-amber-50 border border-amber-300 text-amber-900 px-3 py-2 rounded-sm mb-5"
                data-testid="reset-operator-banner"
              >
                <ShieldAlert size={16} className="mt-0.5 shrink-0" />
                <div className="text-sm">
                  <div className="font-display font-bold uppercase tracking-wide text-xs">
                    Operator account
                  </div>
                  <div className="font-mono text-[11px] text-amber-900/80 mt-0.5">
                    Stricter policy in effect: at least {policy.minLength}{" "}
                    characters, all four character classes, and no
                    brand or email-derived words.
                  </div>
                </div>
              </div>
            )}

            <p className="text-base text-foreground/80 mb-6">
              {policy.isOperator
                ? `Pick a password with at least ${policy.minLength} characters covering all four classes.`
                : `Pick a password with at least ${policy.minLength} characters.`}
            </p>

            <form
              onSubmit={onSubmit}
              className="space-y-4"
              data-testid="reset-form"
              autoComplete="off"
            >
              {/* Single "Show passwords" control above both fields.
                  Keyboard-focusable, ARIA-labelled, toggles both
                  input ``type`` attributes in lockstep. */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setReveal((s) => !s)}
                  className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-ink/60 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-sm px-2 py-1"
                  aria-label={reveal ? "Hide password" : "Show password"}
                  aria-pressed={reveal}
                  data-testid="reset-password-reveal-toggle"
                >
                  {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
                  {reveal ? "Hide passwords" : "Show passwords"}
                </button>
              </div>

              <div>
                <label className="data-sku block mb-2" htmlFor="pw">
                  New password
                </label>
                <input
                  id="pw"
                  type={reveal ? "text" : "password"}
                  required
                  minLength={policy.minLength}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-14 px-4 bg-input border border-border rounded-sm font-body text-base focus:outline-none focus:border-ink"
                  autoComplete="new-password"
                  spellCheck={false}
                  data-testid="reset-password-input"
                />
                <PasswordChecklist
                  value={password}
                  minLength={policy.minLength}
                  requiredClasses={policy.requiredClasses}
                  forbiddenSubstrings={
                    policy.isOperator ? OPERATOR_FORBIDDEN_SUBSTRINGS : []
                  }
                  email={policy.policyEmail}
                  isOperator={policy.isOperator}
                />
              </div>

              <div>
                <label className="data-sku block mb-2" htmlFor="pw2">
                  Confirm password
                </label>
                <input
                  id="pw2"
                  type={reveal ? "text" : "password"}
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full h-14 px-4 bg-input border border-border rounded-sm font-body text-base focus:outline-none focus:border-ink"
                  autoComplete="new-password"
                  spellCheck={false}
                  data-testid="reset-confirm-password-input"
                />
                {confirm.length > 0 && (
                  <div
                    className={`mt-1 font-mono text-[11px] ${
                      matchesOk ? "text-shelf-fresh" : "text-shelf-expired"
                    }`}
                    data-testid="reset-confirm-match-state"
                    data-state={matchesOk ? "match" : "mismatch"}
                  >
                    {matchesOk
                      ? "✓ Passwords match"
                      : tooShort
                        ? "✗ New password is still too short"
                        : "✗ Passwords do not match yet"}
                  </div>
                )}
              </div>

              {err && (
                <div
                  className="bg-shelf-expired-bg text-shelf-expired px-4 py-3 rounded-sm font-mono text-xs"
                  data-testid="reset-error"
                >
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !token}
                className="btn-primary w-full"
                data-testid="reset-submit-button"
              >
                <KeyRound size={18} />{" "}
                {loading ? "Resetting…" : "Reset password"}
              </button>

              <div className="text-center pt-3">
                <Link
                  to="/login"
                  className="font-mono text-xs uppercase tracking-wider text-ink/60 hover:text-ink"
                  data-testid="reset-signin-link"
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          </div>
        )}

        <div className="mt-auto pt-10 data-sku">© ShelfLife · Multi-department</div>
      </div>
      <Toaster position="bottom-center" richColors />
    </div>
  );
}
